import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { WARNING_DIALOG_STYLES, PAYMENT_MESSAGES } from '../constants';

/**
 * Confirmation dialog shown before overriding automated payment values.
 * Displays warnings about what the override entails and records audit info.
 *
 * @param {boolean} open - Whether the dialog is visible
 * @param {function} onClose - Close handler
 * @param {function} onConfirm - Confirm handler (triggers API call)
 * @param {object} dialogConfig - { title, bullets, statusChangeBullet }
 * @param {boolean} isLoading - Whether the override API call is in progress
 */
const OverrideConfirmDialog = ({ open, onClose, onConfirm, dialogConfig, isLoading }) => {
  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (isLoading) return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      {dialogConfig ? (
        <>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmberIcon sx={{ color: WARNING_DIALOG_STYLES.ICON_COLOR }} />
            {dialogConfig.title}
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                bgcolor: WARNING_DIALOG_STYLES.BOX_BG,
                borderLeft: WARNING_DIALOG_STYLES.BOX_BORDER,
                p: 2,
                borderRadius: '0 8px 8px 0',
                mb: 2,
              }}
            >
              <Typography variant="body2" fontWeight={600} color={WARNING_DIALOG_STYLES.TEXT_COLOR} gutterBottom>
                These amounts will no longer update automatically when the order changes.
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {dialogConfig.bullets.map((bullet, i) => (
                  <li key={i}>
                    <Typography variant="body2">{bullet}</Typography>
                  </li>
                ))}
                {dialogConfig.statusChangeBullet && (
                  <li>
                    <Typography variant="body2" fontWeight={600} color={WARNING_DIALOG_STYLES.TEXT_COLOR}>
                      {dialogConfig.statusChangeBullet}
                    </Typography>
                  </li>
                )}
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Your name and timestamp will be recorded for audit.
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{ bgcolor: WARNING_DIALOG_STYLES.FOOTER_BG, borderTop: WARNING_DIALOG_STYLES.FOOTER_BORDER, px: 3, py: 2 }}
          >
            <Button onClick={onClose} variant="outlined" color="inherit" disabled={isLoading}>
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              variant="contained"
              color="primary"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {isLoading ? PAYMENT_MESSAGES.PROCESSING : 'Yes, let me edit'}
            </Button>
          </DialogActions>
        </>
      ) : null}
    </Dialog>
  );
};

export default OverrideConfirmDialog;
